import { ArrowRight, CalendarOff, ClipboardCheck, Clock } from 'lucide-react';
import { orgToday, orgYear } from '@/lib/today';
import { Link } from 'react-router-dom';
import { atLeast, formatDuration, periodMonthName, startOfWeek } from '@cfl/shared';

import {
  Alert, AlertDescription, Badge, Button, Card, CardContent, Skeleton,
} from '@/components/cargofl-ui';
import { PageContainer } from '@/components/shell/page-container';
import { PageHeader } from '@/components/shell/page-header';
import { useLeaveBalance, usePendingAbsences } from '@/lib/absence-api';
import { usePendingApprovals } from '@/lib/approvals-api';
import { useWeek } from '@/lib/timesheet-api';
import { useMonth, useYearTotal } from '@/lib/working-time-api';
import type { Session } from '@/lib/session';

/**
 * What needs doing, and where this week stands.
 *
 * Ordered by whose move it is: this person's own week first, then anything waiting
 * on them for somebody else. It is deliberately not a wall of statistics - the job
 * of this page is to answer "is there anything I have to do?" in one screen.
 */
export function OverviewPage({ session }: { session: Session }) {
  const today = orgToday();
  const monday = startOfWeek(today);
  const year = orgYear();

  const { data: week, isPending: weekPending } = useWeek(monday);
  const { data: month } = useMonth(today.slice(0, 7));
  const { data: yearTotal } = useYearTotal();
  const { data: leave } = useLeaveBalance(year);
  const canApprove = atLeast(session.role, 'SUPERVISOR');
  const { data: pendingWeeks } = usePendingApprovals();
  const { data: pendingLeave } = usePendingAbsences();

  const waiting = (pendingWeeks?.length ?? 0) + (pendingLeave?.length ?? 0);

  return (
    <PageContainer>
      <PageHeader
        title={`Good to see you, ${firstName(session)}`}
        description={`Week of ${longDate(monday)} · Monday to Sunday`}
      />

      {/* Only for people who approve, and only when there is something to approve.
          An empty queue does not need a tile telling you it is empty. */}
      {canApprove && waiting > 0 && (
        <Alert>
          <AlertDescription className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
            <ClipboardCheck className="size-4 shrink-0" />
            <span>
              {pendingWeeks?.length ? (
                <>
                  <strong>{pendingWeeks.length}</strong> week
                  {pendingWeeks.length === 1 ? '' : 's'}
                </>
              ) : null}
              {pendingWeeks?.length && pendingLeave?.length ? ' and ' : null}
              {pendingLeave?.length ? (
                <>
                  <strong>{pendingLeave.length}</strong> leave request
                  {pendingLeave.length === 1 ? '' : 's'}
                </>
              ) : null}{' '}
              waiting on you.
            </span>
            <span className="flex gap-2">
              {pendingWeeks?.length ? (
                <Button hint={"Weeks waiting for your approval"} asChild size="sm" variant="outline">
                  <Link to="/approvals">Review weeks</Link>
                </Button>
              ) : null}
              {pendingLeave?.length ? (
                <Button hint={"Leave requests waiting for you"} asChild size="sm" variant="outline">
                  <Link to="/absence">Review leave</Link>
                </Button>
              ) : null}
            </span>
          </AlertDescription>
        </Alert>
      )}

      {/* ---------------------------------------------------------- this week -- */}
      {weekPending ? (
        <Skeleton className="h-48 w-full" />
      ) : (
        week && (
          <Card>
            <CardContent className="space-y-4 py-5">
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <div className="flex items-baseline gap-2">
                  <Clock className="size-4 self-center text-muted-foreground" />
                  <span className="text-sm font-semibold">This week</span>
                  {/* One badge per part: a week crossing a month end is submitted in two. */}
                  {week.periods.map((period) => (
                    <span key={period.start} className="inline-flex items-baseline gap-1">
                      {week.periods.length > 1 && (
                        <span className="text-xs text-muted-foreground">{periodMonthName(period)}</span>
                      )}
                      <WeekBadge status={period.status} />
                    </span>
                  ))}
                </div>
                <div className="flex items-baseline gap-4 text-sm">
                  <span>
                    Recorded{' '}
                    <span className="ts-num text-base font-semibold">
                      {formatDuration(week.totalSeconds)}
                    </span>
                  </span>
                  <Button hint={"Open this week's timesheet"} asChild size="sm">
                    <Link to="/timesheet">
                      Open timesheet
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <div className="grid min-w-[560px] grid-cols-7 gap-2">
                  {week.days.map((day) => {
                    const isToday = day.date === today;
                    const empty = day.totalSeconds === 0;
                    return (
                      <div
                        key={day.date}
                        className={`rounded-lg border p-3 ${
                          isToday ? 'border-primary bg-primary/5' : 'border-border'
                        } ${day.locked ? 'ts-locked' : ''}`}
                      >
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                          {weekdayLabel(day.date)}
                        </div>
                        <div className="ts-num mt-0.5 text-sm font-medium">
                          {Number(day.date.slice(8, 10))}
                        </div>
                        <div
                          className={`ts-num mt-2 text-sm ${empty ? 'text-muted-foreground' : 'font-medium'}`}
                        >
                          {formatDuration(day.totalSeconds)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        )
      )}

      {/* ----------------------------------------------------- month and leave -- */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardContent className="space-y-3 py-5">
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-semibold">This month</span>
              <Button hint={"Open the monthly report"} asChild size="sm" variant="ghost">
                <Link to="/reports">Details</Link>
              </Button>
            </div>
            {month?.totals ? (
              <div className="flex flex-wrap gap-x-8 gap-y-3">
                {/* To-date while the month is running, so a shortfall means "behind
                    so far" rather than "behind on days that have not happened". */}
                <Figure
                  label={month.totals.toDate ? 'Target so far' : 'Target'}
                  value={formatDuration((month.totals.toDate ?? month.totals).expectedSeconds)}
                />
                <Figure
                  label="Worked"
                  value={formatDuration((month.totals.toDate ?? month.totals).actualSeconds)}
                />
                <Figure
                  label={month.totals.toDate ? 'Balance so far' : 'Balance'}
                  value={signed((month.totals.toDate ?? month.totals).balanceSeconds)}
                  tone={
                    (month.totals.toDate ?? month.totals).balanceSeconds < 0
                      ? 'text-destructive'
                      : 'text-primary'
                  }
                />
                {/* The financial year, April to March - the only figure on this
                    page that spans more than a month, and the one the year reports
                    are measured against. */}
                {yearTotal && (
                  <Figure
                    label={`Year ${yearTotal.label}`}
                    value={formatDuration(yearTotal.actualSeconds)}
                  />
                )}
              </div>
            ) : (
              // Said plainly rather than shown as zeros: somebody with no contract
              // has no target, and a balance of "0:00" would look like they were level.
              <p className="text-sm leading-relaxed text-muted-foreground">
                {month?.message ??
                  'No working contract is set up for you yet, so there is no target to compare against. HR can add one.'}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-3 py-5">
            <div className="flex items-baseline justify-between">
              <span className="flex items-center gap-2 text-sm font-semibold">
                <CalendarOff className="size-4 text-muted-foreground" />
                Leave in {year}
              </span>
              <Button hint={"Request leave"} asChild size="sm" variant="ghost">
                <Link to="/absence">Request</Link>
              </Button>
            </div>
            {leave ? (
              <div className="flex flex-wrap gap-x-8 gap-y-3">
                <Figure
                  label="Remaining"
                  value={`${leave.remainingDays} days`}
                  tone={leave.remainingDays <= 0 ? 'text-destructive' : 'text-primary'}
                />
                <Figure label="Approved" value={`${leave.takenDays} days`} />
                {leave.pendingDays > 0 && (
                  <Figure label="Awaiting" value={`${leave.pendingDays} days`} />
                )}
              </div>
            ) : (
              <Skeleton className="h-10 w-full" />
            )}
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
}

export function WeekBadge({ status }: { status: string }) {
  if (status === 'APPROVED') return <Badge>approved</Badge>;
  if (status === 'SUBMITTED') return <Badge variant="secondary">waiting for approval</Badge>;
  if (status === 'DENIED') return <Badge variant="destructive">sent back</Badge>;
  return <Badge variant="outline">not submitted</Badge>;
}

function Figure({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className={`ts-num text-lg font-semibold ${tone ?? ''}`}>{value}</div>
    </div>
  );
}

/** A leading sign, so zero and a shortfall never look alike. */
function signed(seconds: number): string {
  if (seconds === 0) return '0:00';
  return `${seconds < 0 ? '−' : '+'}${formatDuration(Math.abs(seconds))}`;
}

function firstName(session: Session): string {
  return (session.alias ?? session.displayName).split(/\s+/)[0] ?? 'there';
}

const UTC = { timeZone: 'UTC' } as const;

function weekdayLabel(day: string): string {
  return new Date(`${day}T00:00:00.000Z`).toLocaleDateString('en-IN', { weekday: 'short', ...UTC });
}

function longDate(day: string): string {
  return new Date(`${day}T00:00:00.000Z`).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    ...UTC,
  });
}
